import type { FormAsParams } from '@/components/common/services/transform';
import { formToParams } from '@/components/common/services/transform';
import { addEnabledParams } from '@/components/common/services/url';
import { useStore } from '@/components/common/store';
import { appendQueryToUrl } from '@/js/utils/urlUtils';
import { type MaybeRef, computed, unref } from 'vue';

/**
 * Composable for creating an appropriate reactive navigation URL for a hotel.
 *
 * @param url - URL for the hotel
 * @param withDeepLinks - if true the currently set search filters will be added to the URL as well
 * @param staticLinks - if given, the url will always have these queryparams instead of dynamic ones
 * @returns a computed property with the url
 */
export function useHotelUrl(url: MaybeRef<string>, withDeepLinks = false, staticLinks?: FormAsParams) {
	const store = useStore();
	const formData = computed(() => store.state.searchMask);

	function forceDeepLinks() {
		const urlWithParams = addEnabledParams(unref(url));
		if (urlWithParams.includes('?')) {
			return urlWithParams;
		}
		return appendQueryToUrl(unref(url), formToParams(formData.value));
	}

	const hotelUrl = computed(() => {
		if (staticLinks) {
			return appendQueryToUrl(unref(url), staticLinks);
		}
		return withDeepLinks ? forceDeepLinks() : unref(url);
	});

	return {
		hotelUrl,
	};
}
